import React, { useState, useEffect } from 'react';
import { Drawer } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { scrollToSection } from '../utils/scrollUtils';

/**
 * ヘッダーは横幅が限られるため、ラベルは短く保つこと。
 * 長いラベルを入れるとナビが縮んで文字が縦に折り返す。
 */
const MENU = [
  { key: 'problem', label: '課題' },
  { key: 'features', label: '機能' },
  { key: 'reasons', label: '強み' },
  { key: 'pricing', label: '料金' },
  { key: 'faq', label: 'FAQ' },
  { key: 'company', label: '会社概要' },
];

/** ドロワー内は幅に余裕があるので正式名称で見せる */
const DRAWER_MENU = [
  { key: 'problem', label: '課題' },
  { key: 'features', label: '主要機能' },
  { key: 'screens', label: '実際の画面' },
  { key: 'reasons', label: '選ばれる理由' },
  { key: 'pricing', label: '料金プラン' },
  { key: 'flow', label: 'ご利用の流れ' },
  { key: 'faq', label: 'よくあるご質問' },
  { key: 'company', label: '会社概要' },
];

const Logo: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex shrink-0 items-baseline gap-1 whitespace-nowrap"
    aria-label="サクミエ会計 トップへ"
  >
    <span className="text-2xl font-black tracking-tight text-brand-500">サクミエ</span>
    <span className="text-lg font-bold text-ink-800">会計</span>
  </button>
);

const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /** 規約ページなど別ページからでもトップの該当セクションへ飛べるようにする */
  const go = (key: string) => {
    setOpen(false);
    if (pathname !== '/') {
      navigate('/');
      // 遷移後にDOMが描画されてからスクロールする
      window.setTimeout(() => scrollToSection(key), 120);
    } else {
      scrollToSection(key);
    }
  };

  const toTop = () => {
    if (pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur transition-shadow ${
        scrolled ? 'shadow-header' : ''
      }`}
    >
      <div className="container-content">
        <div className="flex h-[68px] items-center justify-between gap-6">
          <Logo onClick={toTop} />

          {/* PC ナビゲーション（shrink-0 と whitespace-nowrap が無いと文字が縦に折り返す） */}
          <nav className="hidden shrink-0 items-center gap-6 lg:flex xl:gap-7">
            {MENU.map((m) => (
              <button
                key={m.key}
                onClick={() => go(m.key)}
                className="whitespace-nowrap text-small font-medium text-body-soft transition-colors hover:text-ink-900"
              >
                {m.label}
              </button>
            ))}
          </nav>

          <div className="hidden shrink-0 items-center gap-3 lg:flex">
            {/* 副ボタンは横幅に余裕がある画面でのみ表示する */}
            <a href="/monitor.html" className="btn btn-md btn-secondary hidden xl:inline-flex">
              資料・お問い合わせ
            </a>
            <a href="/monitor.html" className="btn btn-md btn-primary">
              30日間無料体験
            </a>
          </div>

          {/* モバイル */}
          <button
            className="p-2 text-ink-800 lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="メニューを開く"
          >
            <MenuOutlined style={{ fontSize: 20 }} />
          </button>
        </div>
      </div>

      <Drawer
        placement="right"
        open={open}
        onClose={() => setOpen(false)}
        closeIcon={<CloseOutlined />}
        title={<span className="font-bold text-ink-900">メニュー</span>}
        width={300}
        styles={{ body: { padding: '8px 0 24px' } }}
      >
        <nav className="flex flex-col">
          {DRAWER_MENU.map((m) => (
            <button
              key={m.key}
              onClick={() => go(m.key)}
              className="border-b border-line px-6 py-4 text-left font-medium text-ink-800"
            >
              {m.label}
            </button>
          ))}
        </nav>
        <div className="mt-6 flex flex-col gap-3 px-6">
          <a href="/monitor.html" className="btn btn-md btn-primary w-full">
            30日間無料体験を申し込む
          </a>
          <a href="/monitor.html" className="btn btn-md btn-secondary w-full">
            資料・お問い合わせ
          </a>
        </div>
      </Drawer>
    </header>
  );
};

export default Header;
